<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <aside class="layout-sidebar">
    <div class="sidebar-header">
      <!-- Slot for custom logo, with default -->
      <slot name="logo" :isSlimMode="isSlimMode">
        <router-link to="/" class="sidebar-logo">
          <img
            v-if="!isSlimMode"
            :src="logoFull"
            :alt="logoAlt"
            class="logo-full"
          />
          <img v-else :src="logoIcon" :alt="logoAlt" class="logo-icon" />
        </router-link>
      </slot>
      <!-- Anchor/Pin button for drawer mode -->
      <button
        v-if="isDrawer && (layoutState.sidebarActive || layoutState.anchored)"
        class="anchor-button"
        @click="toggleAnchor"
      >
        <i
          :class="layoutState.anchored ? 'ri-pushpin-fill' : 'ri-pushpin-line'"
        ></i>
      </button>
    </div>

    <!-- Profile en position START -->
    <SidebarProfileSection
      v-if="
        layoutState.sidebarProfileShow &&
        layoutState.sidebarProfilePosition === 'start'
      "
      :isSlimMode="isSlimMode"
    />

    <div class="sidebar-content">
      <AppSubmenu :items="menuItems" />
    </div>

    <!-- Profile en position END (au-dessus du footer) -->
    <SidebarProfileSection
      v-if="
        layoutState.sidebarProfileShow &&
        layoutState.sidebarProfilePosition === 'end'
      "
      :isSlimMode="isSlimMode"
    />

    <!-- Slot for sidebar footer (entirely customizable) -->
    <slot name="footer"></slot>
  </aside>
</template>

<script>
import { computed } from "vue";
import { useLayout } from "@/composables/useLayout";
import AppSubmenu from "./AppSubmenu.vue";
import SidebarProfileSection from "./SidebarProfileSection.vue";

export default {
  name: "AppSidebar",
  components: {
    AppSubmenu,
    SidebarProfileSection,
  },
  props: {
    /**
     * Menu items array to display in the sidebar
     * Each item: { label, labelKey, icon, to, badge, items }
     */
    menuItems: {
      type: Array,
      default: () => [],
    },
    /**
     * Full logo image path (shown when sidebar is expanded)
     * Can be overridden with the #logo slot
     */
    logoFull: {
      type: String,
      default: "",
    },
    /**
     * Icon logo image path (shown when sidebar is collapsed)
     * Can be overridden with the #logo slot
     */
    logoIcon: {
      type: String,
      default: "",
    },
    /**
     * Logo alt text
     */
    logoAlt: {
      type: String,
      default: "Logo",
    },
  },
  setup() {
    const {
      layoutState,
      isSlim,
      isSlimPlus,
      isDrawer,
      isSmallScreen,
      toggleAnchor,
    } = useLayout();

    const isSlimMode = computed(() => {
      // On small screen, never use slim mode — always show full sidebar content
      if (isSmallScreen.value) {
        return false;
      }
      // Slim mode for slim, slim-plus, or drawer (when not expanded)
      if (isDrawer.value) {
        return !layoutState.sidebarActive && !layoutState.anchored;
      }
      return isSlim.value || isSlimPlus.value;
    });

    return {
      layoutState,
      isSlim,
      isSlimPlus,
      isDrawer,
      toggleAnchor,
      isSlimMode,
    };
  },
};
</script>

<style lang="scss" scoped>
.layout-sidebar {
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  width: var(--sidebar-width, 16rem);
  background-color: var(--sidebar-bg, var(--p-content-background));
  border-right: 1px solid var(--p-content-border-color);
  display: flex;
  flex-direction: column;
  z-index: 999;
  transition:
    width 0.3s ease,
    transform 0.3s ease,
    background-color 0.3s ease;
  overflow: hidden;
}

.sidebar-header {
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

.anchor-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background-color: transparent;
  border-radius: var(--p-border-radius);
  color: var(--p-text-color);
  cursor: pointer;
  transition:
    background-color 0.2s,
    color 0.2s;

  &:hover {
    background-color: var(--p-content-hover-background);
  }

  i {
    font-size: 1rem;
  }
}

.sidebar-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  border: none;
  outline: none;

  &:focus {
    outline: none;
  }
}

.logo-full {
  height: 2.5rem;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  border: none;
  outline: none;
}

.logo-icon {
  height: 2.5rem;
  width: 2.5rem;
  object-fit: contain;
  border: none;
  outline: none;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem 0 1rem 0;
}

.sidebar-footer {
  padding: 1rem;
  border-top: 1px solid var(--p-content-border-color);
}

.footer-logo {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pristy-logo {
  max-width: 100%;
  height: auto;
  max-height: 4rem;
  object-fit: contain;
}
</style>
